import { clearStorage, getStorage, setStorage } from '@getjust/commons';
import { useAtom } from 'jotai';
import { createContext, useContext, useEffect, useState } from 'react';

import { authStateAtom } from '$business/atoms/auth';
import { useLoadingAtom } from '$hooks/state/useLoadingAtom';
import { USE_AUTH_LEGACY } from '$src/constants';
import { AnalyticsEventName } from '$src/hooks/client/useTrackAnalytics';
import { setAccessToken, setRefreshToken } from '$src/http';
import { LegacyData } from '$src/types';

import { useTrackTransition } from './client/useTrackTransition';

export enum AuthStatuses {
  PENDING = 'pending',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
}

interface Context {
  authState: AuthStatuses;
  trySign: () => Promise<void>;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setLegacyState: (data: LegacyData) => void;
}

function getCookie(name: string) {
  const escape = (s: string) => s.replace(/([.*+?^$(){}|[\]/\\])/g, '\\$1');
  const match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
  return match ? match[1] : null;
}

export const authContext = createContext<Context | null>(null);

export const useAuth = () => useContext(authContext) as Context;

export const useProvideAuth = () => {
  const [authState, setAuthState] = useAtom(authStateAtom);
  const setIsAuthenticated = (isAuthenticated: boolean) =>
    setAuthState(isAuthenticated ? AuthStatuses.AUTHENTICATED : AuthStatuses.UNAUTHENTICATED);
  const [isLocalStorageAuthSuccess, setIsLocalStorageAuthSuccess] = useState(false);

  const trySign = async () => {
    try {
      if (USE_AUTH_LEGACY) {
        const token = getStorage<LegacyData>('localStorage', 'token');
        if (!token?.accessToken) throw new Error('No accessToken found.');
        setLegacyState(token);
      } else {
        const infoCookieScopped = getCookie('JUSTC_ITOKEN_' + process.env.NEXT_PUBLIC_ENV);
        const infoCookie = getCookie('JUSTC_ITOKEN');
        if (!infoCookie || !infoCookieScopped) throw new Error('No cookie found.');
      }
      setIsAuthenticated(true);
      setIsLocalStorageAuthSuccess(true);
    } catch {
      setIsAuthenticated(false);
    }
  };

  const setLegacyState = (data: { accessToken: string; refreshToken: string }) => {
    setAccessToken(data.accessToken);
    setRefreshToken(data.refreshToken);
    if (!data.accessToken) {
      clearStorage('localStorage', 'token');
    } else {
      setStorage<LegacyData>('localStorage', 'token', data);
    }
  };

  useTrackTransition({
    value: authState === AuthStatuses.AUTHENTICATED,
    trueEventName: AnalyticsEventName.CUSTOMER_LOGGED_IN,
    falseEventName: AnalyticsEventName.CUSTOMER_LOGGED_OUT,
    skipInitial: true,
  });

  useTrackTransition({
    value: isLocalStorageAuthSuccess,
    trueEventName: AnalyticsEventName.LOCALSTORAGE_AUTH_SUCCESS,
    skipInitial: true,
  });

  return {
    authState,
    trySign,
    setIsAuthenticated,
    setLegacyState,
  };
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const auth = useProvideAuth();
  const [ready, setReady] = useState(false);
  const { onLoadingChange } = useLoadingAtom();

  useEffect(() => {
    const tryFn = async () => {
      await auth.trySign();
      setReady(true);
    };
    tryFn();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onLoadingChange(!ready);
  }, [ready]);

  return (
    ready && (
      <>
        <authContext.Provider value={auth}>
          <>{children}</>
        </authContext.Provider>
      </>
    )
  );
};
